import React, { useState } from 'react';
import { Trans, useTranslation } from '../../i18n';
import {
    CultureFitCategory,
    InfoTableData,
} from '../../typings';
import {
    getCandidateHighestCultureCategory,
} from '../CultureFitWorkPreferences/utils';
import collaborateIcon from '@public/icons/culture-fit/collaborate-icon.png';
import pleasantAtmosphereIcon from '@public/icons/culture-fit/pleasant-atmosphere-icon.png';
import stableEnvironmentIcon from '@public/icons/culture-fit/stable-environment-icon.png';
import appreciationIcon from '@public/icons/culture-fit/appreciation-icon.png';
import challengeIcon from '@public/icons/culture-fit/challenge-icon.png';
import leadershipIcon from '@public/icons/culture-fit/leadership-icon.png';
import resultsIcon from '@public/icons/culture-fit/results-icon.png';
import freedomIcon from '@public/icons/culture-fit/freedom-icon.png';
import inspireIcon from '@public/icons/culture-fit/inspire-icon.png';
import creativityIcon from '@public/icons/culture-fit/creativity-icon.png';
import informalAtmosphereIcon from '@public/icons/culture-fit/informal-atmosphere-icon.png';
import innovateIcon from '@public/icons/culture-fit/innovate-icon.png';
import proceduresIcon from '@public/icons/culture-fit/procedures-icon.png';
import qualityIcon from '@public/icons/culture-fit/quality-icon.png';
import structuredWorkIcon from '@public/icons/culture-fit/structured-work-icon.png';
import thoughtfulIcon from '@public/icons/culture-fit/thoughtful-icon.png';
import globalStyles from '../../styles/global.module.css';
import styles from './styles.module.css';
import InfoTable from '../../components/InfoTable';
import QuestionsToAskModal from '../../components/QuestionsToAskModal';
import { useReportData } from '../../GlobalStateContext';

interface CultureFitThriveAndStruggleProps {
    companyName: string;
    cultureFitCategories: CultureFitCategory[];
    isWeb: boolean;
}

const CultureFitThriveAndStruggle = (
    props: CultureFitThriveAndStruggleProps,
) => {
    const { companyName, cultureFitCategories, isWeb} = props;
    const { t } = useTranslation();
    const [showQuestionsModal, setShowQuestionsModal] =
        useState<boolean>(false);

    const { state } = useReportData();
    const { baselineRange } = state.reportData;

    const candidateHighestCategory = getCandidateHighestCultureCategory(
        cultureFitCategories,
        baselineRange,
    );

    const categoryIcons = {
        family: [
            {
                src: collaborateIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.FAMILY_CULTURE.COLLABORATION',
                )}`,
            },
            {
                src: pleasantAtmosphereIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.FAMILY_CULTURE.GOOD_ATMOSPHERE',
                )}`,
            },
            {
                src: stableEnvironmentIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.FAMILY_CULTURE.STABLE_ENVIRONMENT',
                )}`,
            },
            {
                src: appreciationIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.FAMILY_CULTURE.APPRECIATION',
                )}`,
            },
        ],
        market: [
            {
                src: challengeIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.MARKET_CULTURE.CHALLENGE',
                )}`,
            },
            {
                src: leadershipIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.MARKET_CULTURE.TAKING_THE_LEAD',
                )}`,
            },
            {
                src: resultsIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.MARKET_CULTURE.RESULTS_DRIVEN',
                )}`,
            },
            {
                src: freedomIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.MARKET_CULTURE.FREEDOM',
                )}`,
            },
        ],
        adhocracy: [
            {
                src: inspireIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.ADHOCRACY_CULTURE.INSPIRE',
                )}`,
            },
            {
                src: informalAtmosphereIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.ADHOCRACY_CULTURE.INFORMAL_ATMOSPHERE',
                )}`,
            },
            {
                src: innovateIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.ADHOCRACY_CULTURE.INNOVATION',
                )}`,
            },
            {
                src: creativityIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.ADHOCRACY_CULTURE.CREATIVITY',
                )}`,
            },
        ],
        hierarchy: [
            {
                src: qualityIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.HIERARCHY_CULTURE.QUALITY',
                )}`,
            },
            {
                src: thoughtfulIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.HIERARCHY_CULTURE.THOUGHTFUL',
                )}`,
            },
            {
                src: structuredWorkIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.HIERARCHY_CULTURE.ORDERLY_WORK',
                )}`,
            },
            {
                src: proceduresIcon,
                label: `${t(
                    'CULTURAL_FIT.WHERE_YOU_THRIVE.HIERARCHY_CULTURE.PROCEDURES',
                )}`,
            },
        ],
    };

    const icons = categoryIcons[candidateHighestCategory.id];

    const whereYouThriveBlock = () => {
        const iconsHtml = icons.map((icon, index) => (
            <div className={styles.iconsBlockListWrap} key={`icon-${index}`}>
                <div className={styles.iconsBlockListWrapContainer}>
                    <img src={icon.src} alt="icon" />
                </div>
                <span
                    className={`${globalStyles.text} ${styles.iconsBlockListWrapText}`}>
                    {icon.label}
                </span>
            </div>
        ));

        return (
            <div>
                <div className={`${styles.iconsBlock}`}>
                    <div className={`${styles.iconsBlockList}`}>
                        {iconsHtml}
                    </div>
                </div>
            </div>
        );
    };

    const cultureFitThriveBenefitsChallenges = () => {
        const tableData: InfoTableData = {
            type: 'BENEFITS_CHALLENGES',
            leftColumn: {
                title: t(`CULTURAL_FIT.WHERE_YOU_THRIVE.BENEFITS_TITLE`),
                data: [
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.BENEFITS_DESC_1`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.BENEFITS_DESC_2`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.BENEFITS_DESC_3`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.BENEFITS_DESC_4`,
                        ),
                    },
                ],
            },
            rightColumn: {
                title: t(`CULTURAL_FIT.WHERE_YOU_THRIVE.CHALLENGES_TITLE`),
                data: [
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.CHALLENGES_DESC_1`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.CHALLENGES_DESC_2`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.CHALLENGES_DESC_3`,
                        ),
                    },
                    {
                        description: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.CHALLENGES_DESC_4`,
                        ),
                    },
                ],
            },
        };
        return <InfoTable isWeb={isWeb} tableData={tableData} />;
    };

    return (
        <>
            <h2
                className={`${globalStyles.textSubtitle} ${globalStyles.textNoAboveSpace} ${globalStyles.textNoBelowSpace}`}>
                {t('CULTURAL_FIT.WHERE_YOU_THRIVE.TITLE')}
            </h2>
            <br></br>
            <span className={`${globalStyles.text}`}>
                <Trans
                    i18nKey="CULTURAL_FIT.WHERE_YOU_THRIVE.BLOCK_TITLE"
                    values={{
                        category: t(
                            `CULTURAL_FIT.WHERE_YOU_THRIVE.${candidateHighestCategory.id.toUpperCase()}_CULTURE.${candidateHighestCategory.id.toUpperCase()}`,
                        ),
                    }}
                    components={{ bold: <strong />, italic: <i /> }}
                />
            </span>
            {whereYouThriveBlock()}
            <p className={`${globalStyles.text}`}>
                {t(
                    `CULTURAL_FIT.WHERE_YOU_CAN_GROW.${candidateHighestCategory.id.toUpperCase()}_CULTURE_1`,
                )}
            </p>
            <p className={`${globalStyles.text}`}>
                {t(
                    `CULTURAL_FIT.WHERE_YOU_CAN_GROW.${candidateHighestCategory.id.toUpperCase()}_CULTURE_2`,
                )}
            </p>
            <br></br>
            {cultureFitThriveBenefitsChallenges()}
            <br></br>
            {isWeb && (
                <p
                    className={`${globalStyles.text}`}
                    style={{ cursor: 'pointer', color: '#00A5AC' }}
                    onClick={() => setShowQuestionsModal(true)}>
                    <Trans
                        i18nKey="CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.TITLE"
                        components={{ bold: <strong /> }}
                    />
                </p>
            )}
            {showQuestionsModal && isWeb && (
                <QuestionsToAskModal
                    title={t('CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.TITLE')}
                    descriptions={[
                        t('CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.INTRO_TEXT', {
                            company_name: companyName,
                        }),
                    ]}
                    items={[
                        t(
                            `CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.${candidateHighestCategory.id.toUpperCase()}_CULTURE.QUESTION_1`,
                        ),
                        t(
                            `CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.${candidateHighestCategory.id.toUpperCase()}_CULTURE.QUESTION_2`,
                        ),
                        t(
                            `CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.${candidateHighestCategory.id.toUpperCase()}_CULTURE.QUESTION_3`,
                        ),
                        t(
                            `CULTURAL_FIT.QUESTIONS_YOU_CAN_ASK.${candidateHighestCategory.id.toUpperCase()}_CULTURE.QUESTION_4`,
                        ),
                    ]}
                    closeScanDetails={() => setShowQuestionsModal(false)}
                />
            )}
        </>
    );
};

export default CultureFitThriveAndStruggle;
